import { FC, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FormLayout, useForm } from "antd/lib/form/Form";
import { Button, Checkbox, Col, DatePicker, Form, Input, Row, Select } from "antd";
import moment from "moment";

import styles from "../OutletManagementPage/OutletManagementPage.module.css";

import { useAppSelector } from "app/hooks/hooks";
import { PageTitles, Routes as ROUTES } from "constants/global";
import { Head } from "components/molecules/Head/Head";
import PageTitle from "components/molecules/PageTitle/PageTitle";
import Label from "components/atoms/Label/Label";
import Spinner from "components/atoms/Spinner/Spinner";
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";
import { AIR_LAND_OPTIONS, DATE_FORMAT, FIELD_CONFIG, FORM_CONFIG, TERMINAL_OPTIONS } from "../config";
import { createOutletSelector, getOutletSelector } from "redux/userList/userSlice";
import useCreateOrUpdateOutlet from "../hooks/useCreateOrUpdateOutlet";
import useGetOutlet from "../hooks/useGetOutlet";
import Outlet from "../types/Outlet";
import CustomTagProps from "components/atoms/Tag/types/CustomTagProps";
import Tag from "components/atoms/Tag/Tag";
import useGetFormOptions from "../hooks/useGetFormOptions";

const { validateMessages, layout, validateTrigger } = FORM_CONFIG;

const EditOutletPage: FC = () => {
	const { outletId } = useParams();
	const navigate = useNavigate();
	const [form] = useForm();
	const { tags, categoryType3Tags } = useGetFormOptions();
	const { getOutletData } = useGetOutlet();
	const { createOrUpdateOutlet } = useCreateOrUpdateOutlet();
	const { outlet, loading: isGettingOutlet } = useAppSelector(getOutletSelector);
	const { loading: isCreatingOutlet } = useAppSelector(createOutletSelector);
	const [isReadOnly, setIsReadOnly] = useState(true);
	const [isFormValid, setIsFormValid] = useState(false);
	const [isParticipating, setIsParticipating] = useState(false);

	const isLoading = isGettingOutlet === 1 || isCreatingOutlet === 1;

	const onFinish = async (values: Outlet) => {
		try {
			await createOrUpdateOutlet([values], "update");
			CountDownModal({
				timer: 3,
				title: "Outlet details updated successfully!",
				onTimeComplete: () => {
					setIsReadOnly(true);
				}
			});
		} catch (e) {
			CountDownModal({
				timer: 3,
				errorIcon: true,
				title: "Unable to proceed."
			});
		}
	};

	const checkFormValidity = async () => {
		try {
			// Wait for field validation to complete
			await form.validateFields();
			const formValues: Outlet = form.getFieldsValue();
			const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
			const requiredFields = Object.keys(FIELD_CONFIG).filter((key) => {
				if (key === "vendorCode") {
					return formValues.participatingInChangiEv;
				}
				return FIELD_CONFIG[key].required;
			});

			const allRequiredFieldsFilled = requiredFields.every((field) => {
				const value = formValues[field as keyof Outlet];
				return value !== undefined && value !== "";
			});

			setIsFormValid(!hasErrors && allRequiredFieldsFilled);
		} catch (error) {
			setIsFormValid(false);
		}
	};

	const handleValuesChange = async (changedValues: any) => {
		if ("participatingInChangiEv" in changedValues) {
			setIsParticipating(changedValues.participatingInChangiEv);
			// Mark field as touched and trigger validation immediately
			form.setFields([
				{
					name: "vendorCode",
					touched: true,
					validating: true
				}
			]);
		}
		// Always validate after any change
		await checkFormValidity();
	};

	const setFormValues = () => {
		const participatingValue = outlet.participating === "true";
		setIsParticipating(participatingValue);

		form.setFieldsValue({
			storeId: outlet.store_id ?? "",
			outletCode: outlet.tenant_no ?? "",
			smStoreId: outlet.sm_store_id,
			tenantCode: outlet.cust_no,
			tenantName: outlet.cust_name ?? "",
			rentalSerialNo: outlet.rental_serial_no,
			outletName: outlet.outlet,
			vendorCode: outlet.vendor_code,
			concession: outlet.concession,
			terminal: outlet.terminal === "" ? undefined : outlet.terminal,
			location: outlet.location,
			effectiveDate: outlet.eff_date ? moment(outlet.eff_date, DATE_FORMAT) : "",
			expiryDate: outlet.expiry_date ? moment(outlet.expiry_date, DATE_FORMAT) : "",
			businessCategory: outlet.business_cat,
			productCategory: outlet.prod_category,
			categoryType1: outlet.category_type_1,
			categoryType2: outlet.category_type_2,
			categoryType3: outlet.category_type_3,
			categoryType4: outlet.category_type_4,
			outletStatus: outlet.active === "true",
			participatingInChangiEv: participatingValue,
			unitNo: outlet.unit_no,
			airLand: outlet.air_land === "" ? undefined : outlet.air_land,
			trade: outlet.trade,
			tags: outlet.tag ?? [],
			originalTags: outlet.tag ?? []
		});
	};

	const tagRender = (props: CustomTagProps) => {
		const { label, onClose } = props;
		return (
			<Tag onClose={onClose} closable={!isReadOnly}>
				{label}
			</Tag>
		);
	};

	const renderForm = () => (
		<div className="p-9">
			<Form
				form={form}
				requiredMark={false}
				layout={layout as FormLayout}
				validateTrigger={validateTrigger}
				validateMessages={validateMessages}
				onValuesChange={handleValuesChange}
				onFinish={onFinish}
			>
				<div>
					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={6}>
							<Form.Item rules={[{ required: FIELD_CONFIG["outletStatus"].required }]} name="outletStatus" valuePropName="checked">
								<Checkbox disabled={isReadOnly}>
									<span className="text-base">{FIELD_CONFIG["outletStatus"].label}</span>
								</Checkbox>
							</Form.Item>
						</Col>
						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item rules={[{ required: FIELD_CONFIG["participatingInChangiEv"].required }]} name="participatingInChangiEv" valuePropName="checked">
								<Checkbox disabled={isReadOnly}>
									<span className="text-base">{FIELD_CONFIG["participatingInChangiEv"].label}</span>
								</Checkbox>
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={6}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["storeId"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["storeId"].required} label={FIELD_CONFIG["storeId"].label} classes={{ label: styles?.["form-label"] }} />}
								name="storeId"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["storeId"].label} disabled />
							</Form.Item>
						</Col>

						<Col className="gutter-row" xs={24} md={12}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["smStoreId"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["smStoreId"].required} label={FIELD_CONFIG["smStoreId"].label} classes={{ label: styles?.["form-label"] }} />}
								name="smStoreId"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["smStoreId"].label} maxLength={50} disabled />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["tenantCode"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["tenantCode"].required} label={FIELD_CONFIG["tenantCode"].label} classes={{ label: styles?.["form-label"] }} />}
								name="tenantCode"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["tenantCode"].label} maxLength={20} disabled={isReadOnly} />
							</Form.Item>
						</Col>
						<Col className="gutter-row" xs={24} md={12}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["tenantName"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["tenantName"].required} label={FIELD_CONFIG["tenantName"].label} classes={{ label: styles?.["form-label"] }} />}
								name="tenantName"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["tenantName"].label} maxLength={100} disabled={isReadOnly} />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["outletCode"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["outletCode"].required} label={FIELD_CONFIG["outletCode"].label} classes={{ label: styles?.["form-label"] }} />}
								name="outletCode"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["outletCode"].label} maxLength={20} disabled />
							</Form.Item>
						</Col>
						<Col className="gutter-row" xs={24} md={12}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["outletName"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["outletName"].required} label={FIELD_CONFIG["outletName"].label} classes={{ label: styles?.["form-label"] }} />}
								name="outletName"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["outletName"].label} maxLength={100} disabled={isReadOnly} />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item
								rules={[{ required: isParticipating }]}
								label={<Label requiredMark={isParticipating} label={FIELD_CONFIG["vendorCode"].label} classes={{ label: styles?.["form-label"] }} />}
								name="vendorCode"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["vendorCode"].label} maxLength={10} disabled={isReadOnly} />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["rentalSerialNo"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["rentalSerialNo"].required} label={FIELD_CONFIG["rentalSerialNo"].label} classes={{ label: styles?.["form-label"] }} />}
								name="rentalSerialNo"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["rentalSerialNo"].label} maxLength={20} disabled />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["effectiveDate"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["effectiveDate"].required} label={FIELD_CONFIG["effectiveDate"].label} classes={{ label: styles?.["form-label"] }} />}
								name="effectiveDate"
							>
								<DatePicker placeholder={FIELD_CONFIG["effectiveDate"].label} className={styles["custom-datepicker"]} disabled={isReadOnly} />
							</Form.Item>
						</Col>

						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["expiryDate"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["expiryDate"].required} label={FIELD_CONFIG["expiryDate"].label} classes={{ label: styles?.["form-label"] }} />}
								name="expiryDate"
							>
								<DatePicker placeholder={FIELD_CONFIG["expiryDate"].label} className={styles["custom-datepicker"]} disabled={isReadOnly} />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={4}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["terminal"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["terminal"].required} label={FIELD_CONFIG["terminal"].label} classes={{ label: styles?.["form-label"] }} />}
								name="terminal"
							>
								<Select popupClassName="custom-dropdown" disabled={isReadOnly} placeholder={FIELD_CONFIG["terminal"].label} options={TERMINAL_OPTIONS} />
							</Form.Item>
						</Col>

						<Col className="gutter-row" xs={24} md={4}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["unitNo"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["unitNo"].required} label={FIELD_CONFIG["unitNo"].label} classes={{ label: styles?.["form-label"] }} />}
								name="unitNo"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["unitNo"].label} maxLength={100} disabled={isReadOnly} />
							</Form.Item>
						</Col>

						<Col className="gutter-row" xs={24} md={6}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["location"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["location"].required} label={FIELD_CONFIG["location"].label} classes={{ label: styles?.["form-label"] }} />}
								name="location"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["location"].label} maxLength={100} disabled={isReadOnly} />
							</Form.Item>
						</Col>

						<Col className="gutter-row" xs={24} md={4}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["airLand"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["airLand"].required} label={FIELD_CONFIG["airLand"].label} classes={{ label: styles?.["form-label"] }} />}
								name="airLand"
							>
								<Select popupClassName="custom-dropdown" placeholder={FIELD_CONFIG["airLand"].label} options={AIR_LAND_OPTIONS} disabled={isReadOnly} />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["concession"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["concession"].required} label={FIELD_CONFIG["concession"].label} classes={{ label: styles?.["form-label"] }} />}
								name="concession"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["concession"].label} maxLength={100} disabled={isReadOnly} />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["trade"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["trade"].required} label={FIELD_CONFIG["trade"].label} classes={{ label: styles?.["form-label"] }} />}
								name="trade"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["trade"].label} maxLength={100} disabled={isReadOnly} />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["businessCategory"].required }]}
								label={
									<Label requiredMark={FIELD_CONFIG["businessCategory"].required} label={FIELD_CONFIG["businessCategory"].label} classes={{ label: styles?.["form-label"] }} />
								}
								name="businessCategory"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["businessCategory"].label} maxLength={100} disabled={isReadOnly} />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["productCategory"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["productCategory"].required} label={FIELD_CONFIG["productCategory"].label} classes={{ label: styles?.["form-label"] }} />}
								name="productCategory"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["productCategory"].label} maxLength={100} disabled={isReadOnly} />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["floorName"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["floorName"].required} label={FIELD_CONFIG["floorName"].label} classes={{ label: styles?.["form-label"] }} />}
								name="floorName"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["floorName"].label} maxLength={50} disabled={isReadOnly} />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["categoryType1"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["categoryType1"].required} label={FIELD_CONFIG["categoryType1"].label} classes={{ label: styles?.["form-label"] }} />}
								name="categoryType1"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["categoryType1"].label} maxLength={100} disabled={isReadOnly} />
							</Form.Item>
						</Col>
						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["categoryType2"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["categoryType2"].required} label={FIELD_CONFIG["categoryType2"].label} classes={{ label: styles?.["form-label"] }} />}
								name="categoryType2"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["categoryType2"].label} maxLength={100} disabled={isReadOnly} />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["categoryType3"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["categoryType3"].required} label={FIELD_CONFIG["categoryType3"].label} classes={{ label: styles?.["form-label"] }} />}
								name="categoryType3"
							>
								<Select
									showArrow
									mode="multiple"
									popupClassName="custom-dropdown"
									disabled={isReadOnly}
									placeholder={FIELD_CONFIG["categoryType3"].label}
									options={categoryType3Tags.map((tag) => ({ value: tag.name }))}
								/>
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["categoryType4"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["categoryType4"].required} label={FIELD_CONFIG["categoryType4"].label} classes={{ label: styles?.["form-label"] }} />}
								name="categoryType4"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["categoryType4"].label} maxLength={100} disabled={isReadOnly} />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={8}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["categoryType5"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["categoryType5"].required} label={FIELD_CONFIG["categoryType5"].label} classes={{ label: styles?.["form-label"] }} />}
								name="categoryType5"
							>
								<Input className={styles["cag-input"]} placeholder={FIELD_CONFIG["categoryType5"].label} maxLength={100} disabled={isReadOnly} />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col className="gutter-row" xs={24} md={12}>
							<Form.Item
								rules={[{ required: FIELD_CONFIG["tags"].required }]}
								label={<Label requiredMark={FIELD_CONFIG["tags"].required} label={FIELD_CONFIG["tags"].label} classes={{ label: styles?.["form-label"] }} />}
								name={"tags"}
							>
								<Select
									popupClassName="custom-dropdown"
									disabled={isReadOnly}
									mode="multiple"
									showArrow
									tagRender={tagRender}
									placeholder={FIELD_CONFIG["tags"].label}
									options={tags.map(({ tag_name: tagName }) => ({
										value: tagName
									}))}
								/>
							</Form.Item>
						</Col>
					</Row>

					<Form.Item hidden name={"originalTags"} />
				</div>

				<div className="mt-8 flex justify-between items-center">
					<Link className="font-bold" to={ROUTES.OUTLET_MANAGEMENT}>
						Go back
					</Link>

					{isReadOnly ? (
						<Button className={styles?.["btn-primary"]} onClick={() => setIsReadOnly(false)} aria-label="edit">
							Edit Outlet
						</Button>
					) : (
						<Form.Item shouldUpdate>
							{() => (
								<Button className={styles?.["btn-primary"]} htmlType="submit" disabled={!form.isFieldsTouched() || !isFormValid} aria-label="submit">
									Update Outlet
								</Button>
							)}
						</Form.Item>
					)}
				</div>

				{window.location.hostname === "localhost" && (
					<Form.Item shouldUpdate>
						{() => (
							<>
								<div className="mt-9 p-5 bg-grey-25">
									<p className="font-bold mb-5">Form errors</p>
									<pre>
										{JSON.stringify(
											form.getFieldsError().filter((field) => field.errors.length > 0),
											null,
											2
										)}
									</pre>
								</div>
								<div className="mt-9 p-5 bg-grey-25">
									<p className="font-bold mb-5">Form output</p>
									<pre>
										{JSON.stringify(
											{
												...form.getFieldsValue(),
												effectiveDate: moment(form.getFieldsValue().effectiveDate).format(DATE_FORMAT),
												expiryDate: moment(form.getFieldsValue().expiryDate).format(DATE_FORMAT)
											},
											null,
											2
										)}
									</pre>
								</div>
							</>
						)}
					</Form.Item>
				)}
			</Form>
		</div>
	);

	useEffect(() => {
		checkFormValidity();
	}, [form]);

	useEffect(() => {
		const fetchOutlet = async () => {
			if (!outletId) {
				CountDownModal({
					timer: 3,
					errorIcon: true,
					title: "Unable to proceed",
					onTimeComplete: () => navigate(ROUTES.OUTLET_MANAGEMENT)
				});

				return;
			}

			try {
				await getOutletData(outletId);
			} catch (e) {
				CountDownModal({
					timer: 3,
					errorIcon: true,
					title: "Unable to proceed"
				});
			}
		};

		fetchOutlet();
	}, [outletId]);

	useEffect(() => {
		if (outlet) {
			setFormValues();
		}
	}, [outlet, outletId]);

	return (
		<>
			<Head>{PageTitles.EDIT_OUTLET}</Head>
			<div className={styles.root}>
				{isLoading && <Spinner type="transparent" />}
				<div className="mb-8">
					<PageTitle showBackIcon goBack={() => navigate(ROUTES.OUTLET_MANAGEMENT)} title="Edit/ View Outlet" subTitle="View or edit an outlet's information" />
				</div>

				{!outlet && isGettingOutlet === 0 ? (
					<div className="flex flex-col gap-4 items-center">
						<h3 className={styles["section-header"]}>Unable to proceed.</h3>
						<div>
							<Button className={styles?.["btn-primary"]} onClick={() => navigate(ROUTES.OUTLET_MANAGEMENT)}>
								Go back
							</Button>
						</div>
					</div>
				) : (
					renderForm()
				)}
			</div>
		</>
	);
};

export default EditOutletPage;
